import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { BottomData, Table } from '../styles/styles';
import {
  Header,
  ContentHeading,
  RightContent,
  Content,
  DateDropBox,
  DropdownButton,
  LeftContent,
  HeaderLeft,
  TableSection,
  HeaderTitle,
  HeaderRight,
  BoxWrapper
} from '../styles';
import { ProjectDetailInterface } from '../../../interfaces/ProjectInterface';
import { CustomerDetailsInterface } from '../../../interfaces/CustomerInterface';
import isEmpty from 'lodash/isEmpty';
import { getCustomerList } from '../../../services/customerServices';
import { InvoiceReportStatusList, currencySymbols, invoiceReportCSVHeader } from '../../../global/row';
import { currencyWithDecimal } from '../../../helpers/common';
import {
  ExportInvoiceDataInterface,
  SelectionRangeInterface,
  StatusListInterface
} from '../../../interfaces/TimeReportInterface';
import {
  BUTTON_TYPE,
  CHIP_ICON_POSITION,
  CHIP_SIZE,
  CHIP_TYPE,
  EMPTY_STATE_SIZE,
  REPORT_INVOICE_LOADINGSTATE_DATA,
  TIME_DATE_SELECTED_TEXT
} from '../../../global/constants';
import { DateRange } from 'react-date-range';
import { getInvoiceReportData } from '../../../services/reportService';
import { CSVLink } from 'react-csv';
import { captureException } from '../../../services/logService';
import { InvoiceReportInterface } from '../../../interfaces/ReportInterface';
import ReportDurationDropdown from '../../../component/dropdowns/reportDurationDropdown';
import ReportLoadingState from '../../../component/loading/reportLoadingState';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { useMobileDevice } from '../../../global/useMobile';
import ResponsiveHeader from '../../../component/responsiveHeader';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import BottomNavigationItem from '../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import Chip from '../../../component/chipComponent';
import Button from '../../../component/button';
import EmptyStateV2 from '../../../component/emptyState';
import dayjs from 'dayjs';

const ReportInvoice: React.FC = () => {
  //Refs
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const outsideRef = useRef<HTMLDivElement | null>(null);

  //mobx store variables
  const { currentUser, workspaceStore, reportStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { invoiceReportData, filteredInvoiceReportData, loading } = reportStore;

  // States
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isCustomDropdownOpen, setIsCustomDropdownOpen] = useState(false);
  const [selectAllCustomer, setSelectAllCustomer] = useState(true);
  const [customerList, setCustomerList] = useState<CustomerDetailsInterface[]>([]);
  const [filteredCustomerList, setFilteredCustomerList] = useState<CustomerDetailsInterface[]>([]);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [projectList, setProjectList] = useState<ProjectDetailInterface[]>([]);
  const [filteredProjectList, setFilteredProjectList] = useState<ProjectDetailInterface[]>([]);
  const [selectAllProject, setSelectAllProject] = useState(true);
  const [searchProject, setSearchProject] = useState('');
  const [selectAllStatus, setSelectAllStatus] = useState(true);
  const [statusList, setStatusList] = useState<StatusListInterface[]>([]);
  const [dateSelectedText, setDateSelectedText] = useState('Custom');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [selectionRange, setSelectionRange] = useState<SelectionRangeInterface>({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  });
  // Other variables
  const [exportData, setExportData] = useState<ExportInvoiceDataInterface[]>([]);
  const isMobile = useMobileDevice();

  // Add useEffect for outside click handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (
        outsideRef.current &&
        !outsideRef.current.contains(targetElement) &&
        !targetElement.closest('.calendar-dropdown')
      ) {
        setIsDateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const applyFilter = useCallback(
    (
      data: InvoiceReportInterface[],
      projects?: ProjectDetailInterface[],
      customers?: CustomerDetailsInterface[],
      status?: StatusListInterface[],
      range?: SelectionRangeInterface
    ) => {
      if (isEmpty(range)) {
        range = selectionRange;
      }
      let filteredData: InvoiceReportInterface[] = JSON.parse(JSON.stringify(data));
      const filteredProjects: string[] = projects?.filter((item) => item?.checked)?.map((x) => x?.id) || [];
      const filteredCustomers: string[] = customers?.filter((item) => item?.checked)?.map((x) => x?.id) || [];
      const filteredStatus: number[] = status?.filter((item) => item?.checked)?.map((x) => x?.key) || [];
      filteredData = filteredData.filter((item) => filteredCustomers.includes(item?.customerId));
      filteredData = filteredData.filter((item) => {
        return (
          item.project.length === 0 || item.project.some((project) => filteredProjects.includes(project['_id'] || ''))
        );
      });
      filteredData = filteredData.filter((item) => filteredStatus.includes(item?.status));
      filteredData = filteredData?.filter(
        (item) =>
          dayjs(item?.paidOn).format('YYYY-MM-DD') >= dayjs(range?.startDate).format('YYYY-MM-DD') &&
          dayjs(item?.paidOn).format('YYYY-MM-DD') <= dayjs(range?.endDate).format('YYYY-MM-DD')
      );
      reportStore.setFilteredInvoiceReportData(filteredData);
      if (filteredData?.length > 0) {
        const data = filteredData?.map((item) => {
          return {
            status: item?.statusText,
            paidOn: dayjs(item?.paidOn).format(currentUser?.dateFormat),
            dueOn: dayjs(item?.dueDate).format(currentUser?.dateFormat),
            invoiceNo: item?.invoiceNumber,
            projects: !isEmpty(item?.project) ? item?.project?.map((project) => project?.name).join(', ') : '',
            rate: currencyWithDecimal(item?.totalAmount),
            discount: currencyWithDecimal(item?.invoiceDiscount)
          };
        });
        setExportData(data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportStore, selectionRange, currentUser?.dateFormat]
  );

  const loadData = useCallback(async () => {
    try {
      if (workspace?.id && !isEmpty(workspace?.id)) {
        reportStore.setLoading(true);
        const result = await Promise.all([getInvoiceReportData(workspace?.id), getCustomerList()]);
        rootStore.invoiceStore.setInvoices(result[0]);
        const projects = rootStore.projectStore.projects || [];
        let customerResponse = result[1];
        const projectsResponse = projects?.map((item) => {
          return { ...item, checked: true };
        }) as unknown as ProjectDetailInterface[];
        customerResponse = customerResponse?.map((item: CustomerDetailsInterface) => {
          return { ...item, checked: true };
        });
        const statusResponse = InvoiceReportStatusList?.map((item: StatusListInterface) => {
          return { ...item, checked: true };
        });
        setStatusList(statusResponse);
        applyFilter(result[0], projectsResponse, customerResponse, statusResponse);
        setProjectList(projectsResponse);
        setFilteredProjectList(projectsResponse);
        setCustomerList(customerResponse);
        setFilteredCustomerList(customerResponse);
      }
    } catch (error) {
      captureException(error);
    } finally {
      reportStore.setLoading(false);
    }
  }, [applyFilter, reportStore, workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onClickonStatus = useCallback((value: boolean) => {
    setIsStatusDropdownOpen(value);
    setIsCustomerDropdownOpen(false);
    setIsProjectDropdownOpen(false);
    setIsCustomDropdownOpen(false);
    setIsDateDropdownOpen(false);
  }, []);

  const onClickonClient = useCallback((value: boolean) => {
    setIsCustomerDropdownOpen(value);
    setIsStatusDropdownOpen(false);
    setIsProjectDropdownOpen(false);
    setIsCustomDropdownOpen(false);
    setIsDateDropdownOpen(false);
  }, []);

  const onClickonProject = useCallback((value: boolean) => {
    setIsProjectDropdownOpen(value);
    setIsCustomerDropdownOpen(false);
    setIsStatusDropdownOpen(false);
    setIsCustomDropdownOpen(false);
    setIsDateDropdownOpen(false);
  }, []);

  const openCustomDropdown = useCallback((value: boolean) => {
    setIsCustomDropdownOpen(value);
    setIsDateDropdownOpen(false);
    setIsProjectDropdownOpen(false);
    setIsCustomerDropdownOpen(false);
    setIsStatusDropdownOpen(false);
  }, []);

  const openDateDropdown = useCallback((value: boolean) => {
    setIsDateDropdownOpen(value);
    setIsStatusDropdownOpen(false);
    setIsProjectDropdownOpen(false);
    setIsCustomerDropdownOpen(false);
    setIsCustomDropdownOpen(false);
  }, []);

  const onSelectAllCustomerClick = useCallback(
    (
      customerList: CustomerDetailsInterface[],
      value: boolean,
      projects: ProjectDetailInterface[],
      statusList: StatusListInterface[]
    ) => {
      setSelectAllCustomer(value);
      let customerListClone: CustomerDetailsInterface[] = JSON.parse(JSON.stringify(customerList || []));
      customerListClone = customerListClone?.map((item) => {
        return { ...item, checked: value };
      });
      setCustomerList(customerListClone);
      setFilteredCustomerList(customerListClone);
      setIsCustomerDropdownOpen(false);
      applyFilter(invoiceReportData, projects, customerListClone, statusList);
    },
    [applyFilter, invoiceReportData]
  );

  const onChangeSearchCustomer = useCallback(
    (value: string) => {
      setSearchCustomer(value);
      let timeout;
      if (!isEmpty(value)) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          const newList =
            customerList?.filter((item: CustomerDetailsInterface) =>
              item?.companyName?.toLowerCase().includes(value?.toLowerCase())
            ) || [];
          setFilteredCustomerList(newList);
        }, 300);
      } else {
        setFilteredCustomerList(customerList);
      }
    },
    [customerList]
  );

  const onCheckCustomer = useCallback(
    (
      allUserList: CustomerDetailsInterface[],
      filteredList: CustomerDetailsInterface[],
      userId: string,
      value: boolean,
      projects: ProjectDetailInterface[],
      statusList: StatusListInterface[]
    ) => {
      const updatedAllList = allUserList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const updatedFilteredList = filteredList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const uncheckedTasks = updatedFilteredList?.find((x) => !x?.checked);
      if (uncheckedTasks || updatedFilteredList?.length === 0) setSelectAllCustomer(false);
      else setSelectAllCustomer(true);
      setCustomerList(updatedAllList);
      setFilteredCustomerList(updatedFilteredList);
      applyFilter(invoiceReportData, projects, updatedAllList, statusList);
      setIsCustomerDropdownOpen(false);
    },
    [applyFilter, invoiceReportData]
  );

  const renderCustomerData = useMemo(() => {
    return (
      <BottomData>
        {isEmpty(searchCustomer) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllCustomer}
            onClick={() => onSelectAllCustomerClick(filteredCustomerList, !selectAllCustomer, projectList, statusList)}
          />
        )}
        {(isEmpty(searchCustomer) ? customerList : filteredCustomerList)?.map((item) => {
          return (
            <DropdownItem
              key={item?.id}
              label={item?.companyName}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={(e) =>
                onCheckCustomer(customerList, filteredCustomerList, item?.id, !item?.checked, projectList, statusList)
              }
            />
          );
        })}
      </BottomData>
    );
  }, [
    searchCustomer,
    selectAllCustomer,
    customerList,
    filteredCustomerList,
    onSelectAllCustomerClick,
    projectList,
    statusList,
    onCheckCustomer
  ]);

  const onSelectAllProjectClick = useCallback(
    (
      projectList: ProjectDetailInterface[],
      value: boolean,
      customers: CustomerDetailsInterface[],
      statusList: StatusListInterface[]
    ) => {
      setSelectAllProject(value);
      let projectListClone: ProjectDetailInterface[] = JSON.parse(JSON.stringify(projectList));
      projectListClone = projectListClone?.map((item) => {
        return { ...item, checked: value };
      });
      setProjectList(projectListClone);
      setFilteredProjectList(projectListClone);
      setIsProjectDropdownOpen(false);
      applyFilter(invoiceReportData, projectListClone, customers, statusList);
    },
    [applyFilter, invoiceReportData]
  );

  const onCheckProject = useCallback(
    (
      allProjectList: ProjectDetailInterface[],
      filteredList: ProjectDetailInterface[],
      projectId: string,
      value: boolean,
      customers: CustomerDetailsInterface[],
      statusList: StatusListInterface[]
    ) => {
      const updatedAllList = allProjectList?.map((item) => {
        return item?.id === projectId ? { ...item, checked: value } : item;
      }, []);

      const updatedFilteredList = filteredList?.map((item) => {
        return item?.id === projectId ? { ...item, checked: value } : item;
      }, []);

      const uncheckedTasks = updatedFilteredList?.find((x) => !x?.checked);
      if (uncheckedTasks || updatedFilteredList?.length === 0) setSelectAllProject(false);
      else setSelectAllProject(true);
      setProjectList(updatedAllList);
      setFilteredProjectList(updatedFilteredList);
      setIsProjectDropdownOpen(false);
      applyFilter(invoiceReportData, updatedAllList, customers, statusList);
    },
    [applyFilter, invoiceReportData]
  );

  const onChangeSearchProject = useCallback(
    (value: string) => {
      setSearchProject(value);
      let timeout;
      if (!isEmpty(value)) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          const newList =
            projectList?.filter((item: { name: string }) => item?.name?.toLowerCase().includes(value?.toLowerCase())) ||
            [];
          setFilteredProjectList(newList);
        }, 500);
      } else {
        setFilteredProjectList(projectList);
      }
    },
    [projectList]
  );

  const renderProjectData = useMemo(() => {
    return (
      // eslint-disable-next-line react/jsx-filename-extension

      <BottomData isBorderRadius>
        {isEmpty(searchProject) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllProject}
            onClick={() => onSelectAllProjectClick(projectList, !selectAllProject, customerList, statusList)}
          />
        )}
        {(isEmpty(searchProject) ? projectList : filteredProjectList)?.map((item) => {
          return (
            <DropdownItem
              key={item?.id}
              label={item?.name}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={(e) =>
                onCheckProject(projectList, filteredProjectList, item?.id, !item?.checked, customerList, statusList)
              }
            />
          );
        })}
      </BottomData>
    );
  }, [
    customerList,
    filteredProjectList,
    onCheckProject,
    onSelectAllProjectClick,
    projectList,
    searchProject,
    selectAllProject,
    statusList
  ]);

  const onSelectAllStatus = useCallback(
    async (
      statusList: StatusListInterface[],
      value: boolean,
      projects: ProjectDetailInterface[],
      customerList: CustomerDetailsInterface[]
    ) => {
      setSelectAllStatus(value);
      const updatedStatusList = statusList?.map((ele) => {
        return { ...ele, checked: value };
      });
      setStatusList(updatedStatusList);
      setIsStatusDropdownOpen(false);
      applyFilter(invoiceReportData, projects, customerList, updatedStatusList);
    },
    [applyFilter, invoiceReportData]
  );

  const onChangeStatus = useCallback(
    async (item: number, value: boolean) => {
      const updatedStatusList = statusList?.map((ele) => {
        return ele?.key === item ? { ...ele, checked: value } : ele;
      });
      const isAllStatusSelected = updatedStatusList.every((user: StatusListInterface) => user.checked);
      setSelectAllStatus(isAllStatusSelected);

      setStatusList(updatedStatusList);
      setIsStatusDropdownOpen(false);
      applyFilter(invoiceReportData, projectList, customerList, updatedStatusList, selectionRange);
    },
    [applyFilter, customerList, invoiceReportData, projectList, selectionRange, statusList]
  );

  const handleSelect = useCallback(
    (ranges: { selection: { startDate: Date; endDate: Date; key: string } }) => {
      setSelectionRange(ranges.selection);
      setDateSelectedText(TIME_DATE_SELECTED_TEXT.CUSTOM);
      applyFilter(invoiceReportData, projectList, customerList, statusList, ranges.selection);
    },
    [applyFilter, customerList, invoiceReportData, projectList, statusList]
  );

  const renderCsvOption = () => {
    return (
      filteredInvoiceReportData?.length > 0 && (
        <CSVLink data={exportData || []} headers={invoiceReportCSVHeader} filename={`invoice_report.csv`}>
          {!isMobile ? (
            <Button
              title='Export as CSV'
              iconName='download-icon'
              variant={BUTTON_TYPE.SECONDARY}
              size='small'
              iconPosition='start'
            />
          ) : (
            <BottomNavigationItem iconName='download-icon' />
          )}
        </CSVLink>
      )
    );
  };

  const renderFilterOptions = useMemo(() => {
    return (
      <ContentHeading>
        <LeftContent>
          <Dropdown
            modalTitle='Status'
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Status'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={isStatusDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => onClickonStatus(!isStatusDropdownOpen)}
                isActive={isStatusDropdownOpen}
              />
            }
            trigger='click'
            onOutsideClick={() => setIsStatusDropdownOpen(false)}>
            <BottomData isBorderRadius>
              <DropdownItem
                label='Select All'
                isCheckBox={true}
                isChecked={selectAllStatus}
                onClick={() => onSelectAllStatus(statusList, !selectAllStatus, projectList, customerList)}
              />
              {statusList?.map((item) => {
                return (
                  <DropdownItem
                    key={item?.key}
                    label={item?.label}
                    isCheckBox={true}
                    isChecked={item?.checked}
                    onClick={() => onChangeStatus(item?.key, !item?.checked)}
                  />
                );
              })}
            </BottomData>
          </Dropdown>
          <Dropdown
            modalTitle='Client'
            onChangeSearch={onChangeSearchCustomer}
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Client'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={isCustomerDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => onClickonClient(!isCustomerDropdownOpen)}
                isActive={isCustomerDropdownOpen}
              />
            }
            trigger='click'
            onOutsideClick={() => setIsCustomDropdownOpen(false)}>
            {renderCustomerData}
          </Dropdown>
          <Dropdown
            modalTitle='Project'
            onChangeSearch={onChangeSearchProject}
            content={
              <Chip
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Project'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={isProjectDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => onClickonProject(!isProjectDropdownOpen)}
                isActive={isProjectDropdownOpen}
              />
            }
            trigger='click'
            className='third-child'
            onOutsideClick={() => setIsProjectDropdownOpen(false)}>
            {renderProjectData}
          </Dropdown>
        </LeftContent>
        <RightContent>
          <Dropdown
            modalTitle='Custom Date'
            isMinWidth={152}
            content={
              <Chip
                title={dateSelectedText}
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                trailingIconName='down-arrow-icon'
                className='custom-dropdown'
                trailingIconClassName={isCustomDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => openCustomDropdown(!isCustomDropdownOpen)}
                isActive={isCustomDropdownOpen}
              />
            }
            trigger='click'
            onOutsideClick={() => setIsCustomDropdownOpen(false)}>
            <ReportDurationDropdown
              buttonRef={buttonRef}
              setSelectionRange={(value) => setSelectionRange(value)}
              setIsStatusDropdownOpen={(value) => setIsCustomDropdownOpen(value as boolean)}
              setDateSelectedText={(value) => setDateSelectedText(value)}
            />
          </Dropdown>
          <DropdownButton isMonthPicker>
            <Chip
              variant={CHIP_TYPE.FILL}
              size={CHIP_SIZE.SMALL}
              iconColorType='primary'
              iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
              title={`${dayjs(selectionRange?.startDate).format('MMM DD')} - ${dayjs(selectionRange?.endDate).format(
                'MMM DD'
              )}`}
              leadingIconName='calendar-icon'
              trailingIconName='down-arrow-icon'
              className='calendar-dropdown'
              trailingIconClassName={isDateDropdownOpen ? 'dropdown-icon ' : ''}
              onClick={() => openDateDropdown(!isDateDropdownOpen)}
              isActive={isDateDropdownOpen}
            />
            {isDateDropdownOpen && (
              <DateDropBox ref={outsideRef}>
                <DateRange
                  editableDateInputs={true}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  ranges={[selectionRange]}
                  maxDate={new Date()}
                />
              </DateDropBox>
            )}
          </DropdownButton>
        </RightContent>
      </ContentHeading>
    );
  }, [
    customerList,
    dateSelectedText,
    handleSelect,
    isCustomDropdownOpen,
    isCustomerDropdownOpen,
    isDateDropdownOpen,
    isProjectDropdownOpen,
    isStatusDropdownOpen,
    onChangeSearchCustomer,
    onChangeSearchProject,
    onChangeStatus,
    onClickonClient,
    onClickonProject,
    onClickonStatus,
    onSelectAllStatus,
    openCustomDropdown,
    openDateDropdown,
    projectList,
    renderCustomerData,
    renderProjectData,
    selectAllStatus,
    selectionRange,
    statusList
  ]);

  return (
    <>
      <AppLayout>
        <BoxWrapper>
          {!isMobile ? (
            <Header>
              <HeaderLeft>
                <HeaderTitle>Invoices</HeaderTitle>
              </HeaderLeft>
              <HeaderRight>
                {!isMobile && renderFilterOptions}
                {renderCsvOption()}
              </HeaderRight>
            </Header>
          ) : (
            <ResponsiveHeader title='Invoices' noMargin={true} />
          )}
          <Content>
            {isMobile && renderFilterOptions}
            {(filteredInvoiceReportData?.length === 0 || !filteredInvoiceReportData) && !loading && (
              <EmptyStateV2
                size={EMPTY_STATE_SIZE.MEDIUM}
                title={'There is no report data.'}
                description={'You have no any report data for this date.Search for another date.'}
                imageUrl={'reports_empty'}
                isAlignedCenter={true}
              />
            )}
            {loading && <ReportLoadingState header={REPORT_INVOICE_LOADINGSTATE_DATA} rowCount={6} />}
            {!loading && filteredInvoiceReportData?.length > 0 && (
              <TableSection>
                <Table isReportInvoice>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>paid on </th>
                      <th>due on</th>
                      <th>invoice no</th>
                      <th>projects name</th>
                      <th>rate</th>
                      <th>discount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoiceReportData?.map((item) => {
                      const currency = currencySymbols.find((x) => x.code === item?.invoiceCurrency);
                      return (
                        <tr key={item['_id']}>
                          <td
                            className={
                              item?.status === 5
                                ? 'paid'
                                : item?.status === 4
                                ? 'partiallyPaid'
                                : item?.status === 3
                                ? 'pending'
                                : item?.status === 2
                                ? 'overDue'
                                : item?.status === 1
                                ? 'draft'
                                : ''
                            }>
                            {item?.statusText}
                          </td>
                          <td>{item?.paidOn ? dayjs(item?.paidOn).format(currentUser?.dateFormat) : 'No Paid Date'}</td>
                          <td>
                            {item?.dueDate ? dayjs(item?.dueDate).format(currentUser?.dateFormat) : 'No Due Date'}
                          </td>
                          <td>{item?.invoiceNumber}</td>
                          <td>{item?.project?.map((project) => project.name).join(', ')}</td>
                          <td>
                            {currency?.symbol}
                            {currencyWithDecimal(item?.totalAmount)}
                          </td>
                          <td>
                            {currency?.symbol}
                            {currencyWithDecimal(item?.totalDiscount)}
                          </td>
                        </tr>
                      );
                    }, [])}
                  </tbody>
                </Table>
              </TableSection>
            )}
          </Content>
        </BoxWrapper>
      </AppLayout>
      <BottomSubNavigation rightElement={renderCsvOption()} />
    </>
  );
};

export default observer(ReportInvoice);
